import { OverviewCards } from "../../types/transactionHistory.types";

interface SummaryCardsProps {
  data: OverviewCards | null;
  loading: boolean;
  heading?: string;
  subtitle?: string;
}

function formatCurrency(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || isNaN(amount)) return "₹0";
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

const SUMMARY_ITEMS = [
  {
    key: "totalGrossRevenue",
    title: "Gross Revenue",
    defaultSubtitle: "Total order",
  },
  {
    key: "totalNetRevenue",
    title: "Net Revenue",
    defaultSubtitle: "Gross minus returns",
    isGreen: true,
  },
  {
    key: "totalOtherExpenses",
    title: "Other Expenses",
    defaultSubtitle: "Expenses added by you",
  },
  {
    key: "totalOnlineCollected",
    title: "Online Collected",
    defaultSubtitle: "Net after returns",
  },
  {
    key: "totalCashCollected",
    title: "Cash Collected",
    defaultSubtitle: "",
  },
  {
    key: "totalReturns",
    title: "Returns / Refunds",
    defaultSubtitle: "",
  },
] as const;

export default function SummaryCards({
  data,
  loading,
  heading = "Summary",
  subtitle,
}: SummaryCardsProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
        <div className="h-5 w-32 bg-gray-200 rounded mb-4 animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-4 bg-white animate-pulse">
              <div className="h-3 w-20 bg-gray-200 rounded mb-2.5" />
              <div className="h-6 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-2.5 w-16 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800">{heading}</h3>
        {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
        {SUMMARY_ITEMS.map(({ key, title, defaultSubtitle, isGreen }) => {
          const card = data[key as keyof OverviewCards];
          const amount = card ? Number(card.amount || 0) : 0;
          const displaySubtitle = card?.subtitle || defaultSubtitle;

          return (
            <div
              key={key}
              className="border border-gray-100 rounded-xl p-4 bg-white hover:border-gray-200 transition-colors"
            >
              <p className="text-xs text-gray-500 font-normal leading-tight">
                {card?.title || title}
              </p>
              <p
                className={`text-xl font-bold tracking-tight mt-1.5 ${
                  isGreen ? "text-emerald-600" : "text-gray-900"
                }`}
              >
                {formatCurrency(amount)}
              </p>
              {displaySubtitle ? (
                <p className="text-[11px] text-gray-400 mt-1 truncate">
                  {displaySubtitle}
                </p>
              ) : (
                <div className="h-[17px]" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
